import { TagChip } from '@/components/tags/TagChip';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { gql } from '@apollo/client';

import { TaggedClinician, clinicianTagsForDisplay } from './clinicianTagsForDisplay';

// A clinician's own tags and the tags of each team they belong to. Both are permission
// gated and come back null for a viewer who cannot read them.
export const ClinicianTagsFragment = gql`
    fragment ClinicianTags on Clinician {
        tags(filters: { isActive: { exact: true } }) {
            items {
                id
                name
            }
        }
        clinicianTeams {
            id
            tags(filters: { isActive: { exact: true } }) {
                items {
                    id
                    name
                }
            }
        }
    }
`;

interface Tag {
    id: string;
    name: string;
}

// Keep each chip on a single line; ellipsis a long label rather than wrapping it taller.
const TRUNCATING_CHIP_SX = {
    maxWidth: '100%',
    '& .MuiChip-label': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
} as const;

const ClinicianTagChips = ({ tags }: { tags: Tag[] }) => {
    if (tags.length === 0) {
        return null;
    }
    return (
        <HorizontalStack
            spacing={0.5}
            fullWidth
            flexWrap='nowrap'
            useFlexGap
            alignItems='center'
            sx={{ minWidth: 0, overflow: 'hidden' }}
        >
            {tags.map((tag) => (
                <TagChip key={tag.id} label={tag.name} size='small' sx={TRUNCATING_CHIP_SX} />
            ))}
        </HorizontalStack>
    );
};

/**
 * Renders a clinician's name with their tag chips stacked underneath, their own tags and
 * their teams' together. Chips only appear when `showTags` is true.
 */
export const ClinicianNameWithTags = ({
    fullName,
    clinician,
    showTags,
}: {
    fullName: string;
    clinician: TaggedClinician;
    showTags: boolean;
}) => {
    if (!showTags) {
        return <>{fullName}</>;
    }
    return (
        <VerticalStack spacing={0.25}>
            <span>{fullName}</span>
            <ClinicianTagChips tags={clinicianTagsForDisplay(clinician)} />
        </VerticalStack>
    );
};
